import { useEffect, useRef } from "react";

/**
 * "Continue with Google" for stores outside India
 * (https://developers.google.com/identity/gsi/web).
 *
 * India signs in with a phone number and an OTP; everywhere else there is no
 * Indian number to text, so the web does what the Android app does there:
 * Google's ID token goes to /users/google_login, which checks it was issued to
 * one of Fleek's own OAuth clients (GOOGLE_SIGNIN_CLIENT_IDS) and answers like
 * verify_otp. REACT_APP_GOOGLE_CLIENT_ID must be that list's web client - the
 * one Android passes as serverClientId (an older google-services.json client is
 * deleted) - with web.getfleek.app among its Authorized JavaScript origins.
 *
 * With REACT_APP_GOOGLE_CLIENT_ID unset this renders nothing.
 */

const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

let scriptPromise = null;

function loadScript() {
  if (typeof window === "undefined" || window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => {
        scriptPromise = null; // allow a retry on the next mount
        reject(new Error("Google sign-in script failed to load"));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/**
 * onCredential(idToken) once Google answers; onError() if the button can't load;
 * onStart() when the button is tapped, before Google's popup.
 */
export const GoogleSignIn = ({ onCredential, onError, onStart, className }) => {
  const containerRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  const onStartRef = useRef(onStart);

  useEffect(() => {
    onCredentialRef.current = onCredential;
    onErrorRef.current = onError;
    onStartRef.current = onStart;
  });

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => onCredentialRef.current?.(response?.credential),
          ux_mode: "popup",
          context: "signin",
        });
        window.google.accounts.id.renderButton(containerRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          shape: "pill",
          text: "continue_with",
          width: Math.min(containerRef.current.offsetWidth || 320, 400),
          click_listener: () => onStartRef.current?.(),
        });
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current?.();
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!GOOGLE_CLIENT_ID) return null;
  return <div ref={containerRef} className={className} />;
};

export default GoogleSignIn;
